/**
 * API Service for Payout Endpoints
 * Proxies through TerraBE to POS Backend payout API
 */

import { api } from 'src/api/config';

const API_BASE_URL = api.hostname;

/**
 * Fetch merchant payouts
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status
 * @param {string} params.payout_date_after - Filter by date
 * @param {string} params.payout_date_before - Filter by date
 * @param {string} token - Auth token
 * @returns {Promise} Array of merchant payouts
 */
export async function fetchMerchantPayouts(params = {}, token) {
  const queryParams = new URLSearchParams();

  if (params.status) queryParams.append('status', params.status);
  if (params.payout_date_after) queryParams.append('payout_date_after', params.payout_date_after);
  if (params.payout_date_before) queryParams.append('payout_date_before', params.payout_date_before);

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/api/v1/payouts/merchant/${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch ISO payouts
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status
 * @param {string} token - Auth token
 * @returns {Promise} Array of ISO payouts
 */
export async function fetchISOPayouts(params = {}, token) {
  const queryParams = new URLSearchParams();

  if (params.status) queryParams.append('status', params.status);

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/api/v1/payouts/iso/${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch the calling merchant admin's own payouts (self-scoped)
 * TerraBE resolves the caller's account server-side; no params accepted.
 * @param {string} token - Auth token
 * @returns {Promise} Payout list (paginated or array)
 */
export async function fetchMyMerchantPayouts(token) {
  const url = `${API_BASE_URL}/api/v1/payouts/merchant/mine/`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch the calling FI admin's own ISO commission payouts (self-scoped)
 * TerraBE resolves the caller's FI server-side; no params accepted.
 * @param {string} token - Auth token
 * @returns {Promise} Payout list (paginated or array)
 */
export async function fetchMyISOPayouts(token) {
  const url = `${API_BASE_URL}/api/v1/payouts/iso/mine/`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch payout batches
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status
 * @param {string} params.batch_type - Filter by type (merchant/iso)
 * @param {string} token - Auth token
 * @returns {Promise} Array of payout batches
 */
export async function fetchPayoutBatches(params = {}, token) {
  const queryParams = new URLSearchParams();

  if (params.status) queryParams.append('status', params.status);
  if (params.batch_type) queryParams.append('batch_type', params.batch_type);

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/api/v1/payouts/batches/${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Approve merchant payouts
 * @param {Array} payoutIds - Array of payout UUIDs
 * @param {string} token - Auth token
 * @returns {Promise} Result with count
 */
export async function approveMerchantPayouts(payoutIds, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/merchant/approve/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ payout_ids: payoutIds }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Hold merchant payouts
 * @param {Array} payoutIds - Array of payout UUIDs
 * @param {string} reason - Hold reason
 * @param {string} token - Auth token
 * @returns {Promise} Result with count
 */
export async function holdMerchantPayouts(payoutIds, reason, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/merchant/hold/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ payout_ids: payoutIds, reason }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Release merchant payouts from hold
 * @param {Array} payoutIds - Array of payout UUIDs
 * @param {string} token - Auth token
 * @returns {Promise} Result with count
 */
export async function releaseMerchantPayouts(payoutIds, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/merchant/release/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ payout_ids: payoutIds }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Generate NACHA batch for merchant payouts
 * @param {Array} payoutIds - Array of approved payout UUIDs
 * @param {string} token - Auth token
 * @returns {Promise} Batch info
 */
export async function generateMerchantBatch(payoutIds, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/batches/generate-merchant/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ payout_ids: payoutIds }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Approve ISO payouts
 * @param {Array} payoutIds - Array of payout UUIDs
 * @param {string} token - Auth token
 * @returns {Promise} Result with count
 */
export async function approveISOPayouts(payoutIds, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/iso/approve/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ payout_ids: payoutIds }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Hold ISO payouts
 * @param {Array} payoutIds - Array of payout UUIDs
 * @param {string} reason - Hold reason
 * @param {string} token - Auth token
 * @returns {Promise} Result with count
 */
export async function holdISOPayouts(payoutIds, reason, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/iso/hold/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ payout_ids: payoutIds, reason }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Generate NACHA batch for ISO payouts
 * @param {Array} payoutIds - Array of approved payout UUIDs
 * @param {string} token - Auth token
 * @returns {Promise} Batch info
 */
export async function generateISOBatch(payoutIds, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/batches/generate-iso/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ payout_ids: payoutIds }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Mark batch as submitted
 * @param {string} batchId - Batch UUID
 * @param {string} token - Auth token
 * @returns {Promise} Updated batch
 */
export async function markBatchSubmitted(batchId, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/batches/${batchId}/mark-submitted/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Mark batch as confirmed
 * @param {string} batchId - Batch UUID
 * @param {string} token - Auth token
 * @returns {Promise} Updated batch
 */
export async function markBatchConfirmed(batchId, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/batches/${batchId}/mark-confirmed/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Release ISO payouts from hold
 * @param {Array} payoutIds - Array of payout UUIDs
 * @param {string} token - Auth token
 * @returns {Promise} Result with count
 */
export async function releaseISOPayouts(payoutIds, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/iso/release/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ payout_ids: payoutIds }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Re-fetch bank info for missing_bank_info merchant payouts
 * @param {Array} payoutIds - Array of payout UUIDs
 * @param {string} token - Auth token
 * @returns {Promise} Result with refreshed count
 */
export async function refreshMerchantBankInfo(payoutIds, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/merchant/refresh-bank-info/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ payout_ids: payoutIds }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Re-read bank info from the ISO record for missing_bank_info ISO payouts
 * @param {Array} payoutIds - Array of payout UUIDs
 * @param {string} token - Auth token
 * @returns {Promise} Result with refreshed count
 */
export async function refreshISOBankInfo(payoutIds, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/iso/refresh-bank-info/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ payout_ids: payoutIds }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Download an xlsx report as a Blob.
 * @param {string} path - Full API path incl. query string,
 *   e.g. '/api/v1/payouts/spreadsheet/merchant/?status=approved'
 * @param {string} token - Auth token
 * @returns {Promise<Blob>} xlsx blob
 */
export async function downloadXlsxReport(path, token) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let message = `HTTP error! status: ${response.status}`;
    try {
      const data = await response.json();
      message = data.error || data.detail || message;
    } catch (_) {
      // non-JSON error body — keep the generic message
    }
    throw new Error(message);
  }

  return response.blob();
}

/**
 * Download NACHA file for a batch
 * @param {string} batchId - Batch UUID
 * @param {string} token - Auth token
 * @returns {Promise} NACHA file content
 */
export async function downloadNACHAFile(batchId, token) {
  const url = `${API_BASE_URL}/api/v1/payouts/batches/${batchId}/download/`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.text();
}
